// ----------------------------------------------------------------------
// <copyright file="Izenda.WorkspaceDetailStore.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  const { isEmpty, map, isNil, omit, get, flatMap, has, isArray, keys, each, find } = require('lodash/fp');

  var m = require('mobx');
  var dataFetcher = require('fetchers/Izenda.WorkspaceDataFetcher');
  var reportFetcher = require('fetchers/Izenda.ReportsDataFetcher');
  var roleSetupFetcher = require('fetchers/Izenda.RoleSetupFetcher');
  var connectionStringDataFetcher = require('fetchers/Izenda.ConnectionStringDataFetcher');
  var uiState = require('logic/Izenda.UiState.js');
  var constants = require('logic/data/Izenda.CopyManagementConstants');
  var localizer = require('share/localization/Izenda.Localizer');
  var dashboardFetcher = require('fetchers/Izenda.DashboardDataFetcher');
  var CategoryDetail = require('logic/models/Izenda.CategoryDetail');
  var LazyLoadingUtils = require('share/utils/Izenda.LazyLoadingUtils');
  var QuerySourceTree = require('logic/models/Izenda.QuerySourceTreeMultiLevel');

  /**
   * It encapsulates the connection detail app logic (side-effect likes API invoke
   * and UI logic)
   * @class WorkspaceDetailStore
   * @param {ConnectionStore} masterStore Connection store
   */
  function WorkspaceDetailStore(masterStore) {
    this.masterStore = masterStore;
    this.onInvalidWorkspace = function(msg) {};

    var props = {
      item: function() {
        return this.masterStore.item;
      },
      tenantId: function() {
        return uiState.getTenantId();
      },
      tenantList: function() {
        return this.masterStore.tenantList;
      }
    };

    m.extendObservable(this, props);
  }
  /**
   * link between store and uiState
   */
  WorkspaceDetailStore.prototype.init = function() {
    uiState.setCheckDirtyFunction(
      function() {
        return this.item.needConfirmWhenLeaving;
      }.bind(this)
    );
  };

  /**
   * unlink between store and uiState
   */
  WorkspaceDetailStore.prototype.destroy = function() {
    uiState.resetCheckDirtyFunction();
  };

  /**
   * Load connection data by its id from API
   * @return {Promise} A promise of loading connection data
   */
  WorkspaceDetailStore.prototype.load = m.action(function(workspaceId) {
    var store = this;
    workspaceId = workspaceId || this.item.id;
    return dataFetcher
      .getWorkspaceDetail(workspaceId)
      .bind(this)
      .then(
        m.action(function(workspaceDetail) {
          // Defer notifying changes until reading finished
          return Promise.all([
            store.item.readDetail(workspaceDetail),
            store.item.getOriginalTenantList(store.tenantList)
          ]);
        })
      )
      .then(this.loadDistinctConnections);
  });

  /**
   * load Distinct Connections
   * @return {promise}
   */
  WorkspaceDetailStore.prototype.loadDistinctConnections = m.action(function() {
    var item = this.item;
    var listTenants = flatMap(function(tenant) {
      return tenant.tenantId;
    }, item.workspaceTenants);

    if (!isEmpty(listTenants)) {
      return dataFetcher.loadDistinctConnections(listTenants).then(function(listConnection) {
        isArray(listConnection) && item.readDistinctConnections(listConnection);
        return Promise.resolve('');
      });
    } else {
      return Promise.resolve(true);
    }
  });

  /**
   * Load connection data by its id from API
   * @return {Promise} A promise of loading connection data
   */
  WorkspaceDetailStore.prototype.loadSourceConnections = m.action(function(sourceId) {
    var item = this.item;
    return connectionStringDataFetcher
      .getConnectionDetailInfo(sourceId, true, { defaultChecked: item.copyDataModel })
      .then(function(result) {
        item.setPropertyValue('sourceHashCode', result.hashCode);
        item.readSourceConnections(result.connections);
        return result;
      });
  });

  /**
   * load Destination Connections
   * @return {Promise} A promise of loading connection data
   */
  WorkspaceDetailStore.prototype.loadDestinationConnections = m.action(function(tenantId) {
    var tenant = find({ tenantId: tenantId }, this.item.workspaceTenants) || this.item.selectedTenant;

    if (tenant) {
      var data = {
        tenantId: tenant.tenantId,
        mappings: tenant.dataMapping
      };
      return dataFetcher.loadDestinationConnections(data).then(function(result) {
        if (isNil(result.messages)) {
          tenant.destinationHashCode = result.hashCode;
          tenant.readDestinationConnections(result.connections);
        }
        return null;
      });
    }

    return Promise.resolve();
  });

  /**
   * save workspace detail
   * @return {Promise} A promise of loading connection data
   */
  WorkspaceDetailStore.prototype.save = function(isSaveAs) {
    var data = this.item.getDataForSave(isSaveAs);

    return dataFetcher.saveWorkspaceDetail(data).then(
      function(result) {
        if (!isNil(result.success) && !result.success) {
          return result;
        } else {
          this.masterStore.loadList();
          this.item.readDetail(result);
          this.item.showAlertMessage('success', localizer.getText('COMMON_MESSAGES_DETAILS_WERE_SAVED'));
          return true;
        }
      }.bind(this)
    );
  };

  /**
   * validate Workspace Tenant
   * @return {Promise} A promise of loading connection data
   */
  WorkspaceDetailStore.prototype.validateWorkspaceTenant = m.action(function(tenant, isDetail) {
    var item = this.item;
    var selectedTenant = tenant || item.selectedTenant;
    var cloneTenant = m.toJS(selectedTenant);
    var mappings = !!item.selectedTenant
      ? cloneTenant.dataMapping
      : cloneTenant.getMappingsForTenant(item.workspaceMappings);

    var data = {
      workspaceTenant: omit(['sourceConnections', 'destinationConnections', 'databaseSources'], cloneTenant),
      mappings: mappings,
      sourceId: item.sourceId,
      sourceHashCode: item.sourceHashCode,
      selectedSources: item.omitProperties(item.mapppingDatabasesSource, 'querySourceFields'),
      selectedReports: m.toJS(get('categories', item.sourceReports)),
      selectedTemplates: m.toJS(get('categories', item.sourceTemplates)),
      selectedDashboards: m.toJS(get('categories', item.sourceDashboards)),
      loadWorkspaceTenantConnections: isDetail
    };

    return dataFetcher.validateWorkspaceTenant(data).then(
      function(result) {
        if (isNil(result.messages)) {
          selectedTenant.poppulateDataAfterValidate(result.workspaceTenant, isDetail);
        }

        return result;
      }.bind(this)
    );
  });

  /**
   * validate Workspace Tenant
   * @return {Promise} A promise of loading connection data
   */
  WorkspaceDetailStore.prototype.goToTenantMode = function(tenant) {
    var item = this.item;

    return connectionStringDataFetcher
      .getConnectionDetailInfo(tenant.tenantId, false, { removeEmptyParent: false })
      .then(function(result) {
        return item.setSelectedTenant(tenant, result.connections);
      });
  };

  /**
   * check connection has data model
   * @return {Promise} A promise of loading connection data
   */
  WorkspaceDetailStore.prototype.checkConnectionHasDataModel = m.action(function() {
    var data = this.item.getAllDestinationConnection();

    return dataFetcher.checkConnectionHasDataModel(data).then(
      function(result) {
        // display Run Copy Data Model Popup if result is not empty
        return !isEmpty(result)
          ? {
              showRunCopyDataModelPopup: true,
              tree: this.item.getMatchedSourceTreeForRunCopy(result, true)
            }
          : null;
      }.bind(this)
    );
  });

  /**
   * detect Conflicting Advanced Settings
   * @return {Promise} A promise of loading connection data
   */
  WorkspaceDetailStore.prototype.detectConflictingAdvancedSettings = m.action(function() {
    var data = this.item.getDataForSave().workspace;

    return dataFetcher
      .detectConflictingAdvancedSettings(data)
      .bind(this)
      .then(function(result) {
        // display Run Copy Advanced Settings Popup if result is not empty
        return !isEmpty(result)
          ? {
              showRunCopyAdvancedSettingsPopup: true,
              tree: this.item.getSourceTreeTenantForRunCopy(result)
            }
          : null;
      });
  });

  /**
   * detect Conflicting Dashboard
   * @return {Promise} A promise of loading connection data
   */
  WorkspaceDetailStore.prototype.detectConflictingDashboards = m.action(function() {
    var data = this.item.getDataForSave().workspace;

    return dataFetcher
      .detectConflictingDashboards(data)
      .bind(this)
      .then(function(result) {
        // display Run Copy Dashboards Popup if result is not empty
        return !isEmpty(result)
          ? {
              showRunCopyDashboardPopup: true,
              tree: this.item.getConflictingDashboardTree(result)
            }
          : null;
      });
  });

  /**
   * detect Conflicting Reports
   * @return {Promise} A promise of loading connection data
   */
  WorkspaceDetailStore.prototype.detectConflictingReports = m.action(function() {
    var data = this.item.getDataForSave().workspace;

    return dataFetcher
      .detectConflictingReports(data)
      .bind(this)
      .then(function(result) {
        // display Run Copy Reports Popup if result is not empty
        return !isEmpty(result)
          ? {
              showRunCopyReportPopup: true,
              tree: this.item.getConflictingReportTree(result)
            }
          : null;
      });
  });

  /**
   * detect Conflicting Reports
   * @return {Promise} A promise of loading connection data
   */
  WorkspaceDetailStore.prototype.detectConflictingTemplates = m.action(function() {
    var data = this.item.getDataForSave().workspace;

    return dataFetcher
      .detectConflictingTemplates(data)
      .bind(this)
      .then(function(result) {
        // display Run Copy Reports Popup if result is not empty
        return !isEmpty(result)
          ? {
              showRunCopyTemplatePopup: true,
              tree: this.item.getConflictingReportTree(result)
            }
          : null;
      });
  });

  /**
   * detect Conflicting Tenant Permissions
   * @return {Promise} A promise of loading connection data
   */
  WorkspaceDetailStore.prototype.detectConflictingTenantPermissions = m.action(function() {
    var data = this.item.getDataForSave().workspace;

    return dataFetcher
      .detectConflictingTenantPermissions(data)
      .bind(this)
      .then(function(result) {
        // display Run Copy Tenant Permission Popup if result is not empty
        return !isEmpty(result)
          ? {
              showRunCopyTenantPermissionPopup: true,
              tree: this.item.getSourceTreeTenantForRunCopy(result)
            }
          : null;
      });
  });

  /**
   * detect Conflicting Role Permission
   * @return {Promise} A promise of loading connection data
   */
  WorkspaceDetailStore.prototype.detectConflictingRolePermission = m.action(function() {
    var data = this.item.getDataForSave().workspace;

    return dataFetcher
      .detectConflictingRolePermission(data)
      .bind(this)
      .then(function(result) {
        // display Run Copy Role Permission Popup if result is not empty
        return !isEmpty(result)
          ? {
              showRunCopyRolePermissionPopup: true,
              tree: this.item.getMatchedSourceTreeForRunCopy(result)
            }
          : null;
      });
  });

  /**
   * run copy workspace detail
   * @return {Promise} A promise of loading connection data
   */
  WorkspaceDetailStore.prototype.runCopy = function() {
    return dataFetcher.runCopy(this.item.getDataForSave()).then(
      function(result) {
        if (result.success) {
          this.item.showAlertMessage('success', constants.RUN_COPY_SUCCESS);
          !isEmpty(result.data) &&
            !isEmpty(result.data.destinationHashCodes) &&
            keys(result.data.destinationHashCodes).forEach(
              function(item) {
                var tenant = find({ tenantId: item }, this.item.workspaceTenants);
                if (tenant) {
                  tenant.destinationHashCode = result.data.destinationHashCodes[item];
                }
              }.bind(this)
            );
        } else if (isEmpty(result.messages[0].key)) {
          this.item.showAlertMessage('error', result.messages[0].messages[0]);
        }

        return result;
      }.bind(this)
    );
  };

  /**
   * validate Workspace Name
   * @return {Promise} A promise after validation workspace name
   */
  WorkspaceDetailStore.prototype.validateWorkspaceName = function(name) {
    return dataFetcher.validateWorkspaceName({
      Id: this.item.id,
      Name: name
    });
  };

  /**
   * check Hash Code Source
   */
  WorkspaceDetailStore.prototype.checkHashCodeSource = m.action(function() {
    var sourceId = this.item.sourceId;

    return Promise.join(
      connectionStringDataFetcher.getConnectionDetailInfo(sourceId, false, {
        defaultChecked: false
      }),
      this.loadDistinctConnections(),
      m.action(
        function(result) {
          this.handleRefreshWorkspace(result);
          if (this.item.copyReport) {
            this.item.getSourceReportsPrev();
            this.item.getSourceTemplatesPrev();
            this.loadReports(sourceId, false);
          }

          if (this.item.copyDashboard) {
            this.item.getSourceDashboardsPrev();
            this.item.resetWorkspaceTenantsStatus();
            this.loadDashboards(sourceId, false);
          }

          return Promise.resolve();
        }.bind(this)
      )
    );
  });

  /**
   * check Hash Code Destination Workspace Tenant
   * @param  {object} workspaceTenant
   */
  WorkspaceDetailStore.prototype.checkHashCodeDestinaWorkspaceTenant = m.action(function(workspaceTenant) {
    return dataFetcher
      .loadDestinationConnections({
        tenantId: workspaceTenant.tenantId,
        mappings: workspaceTenant.dataMapping
      })
      .then(this.handleRefreshWorkspaceTenant.bind(this, workspaceTenant));
  });

  /**
   * handle refresh workspace detail
   * @param  {promise} result
   */
  WorkspaceDetailStore.prototype.handleRefreshWorkspace = m.action(function(result) {
    var selectedTenant = this.item.selectedTenant;
    // check hashcode of every workspace tenant
    if (this.item.sourceHashCode === result.hashCode) {
      return selectedTenant
        ? this.checkHashCodeDestinaWorkspaceTenant(selectedTenant)
        : this.item.workspaceTenants.forEach(this.checkHashCodeDestinaWorkspaceTenant.bind(this));
    } else {
      // bind data source connection
      this.item.readSourceConnections(result.connections);
      // reset status list workspace tenant
      return this.item.resetWorkspaceTenantsStatus();
    }
  });

  /**
   * handle Refresh Workspace Tenant
   * @param  {object} workspaceTenant
   * @param  {promise} result
   */
  WorkspaceDetailStore.prototype.handleRefreshWorkspaceTenant = m.action(function(result, workspaceTenant) {
    if (workspaceTenant.destinationHashCode !== result.hashCode) {
      workspaceTenant.setPropertyValue('destinationHashCode', result.hashCode);
      workspaceTenant.readDestinationConnections(result.connections);
      workspaceTenant.setDirty();
    }
  });

  /**
   * handle after Concurrency occur
   */
  WorkspaceDetailStore.prototype.handleAfterConcurrency = m.action(function(detail) {
    var sourceId = this.item.sourceId;
    this.resetHashCodeAfterConcurrency();

    if (this.item.copyRoles || this.item.copyRolePermission) {
      this.loadRolesAndPermissions(sourceId);
    }

    // Workspace Tenant Source Connection Concurrency
    if (!isNil(detail)) {
      if (detail.source === constants.CONCURRENCY_ERROR.SOURCE) {
        // reload destination connection
        this.item.workspaceTenants.forEach(
          function(item) {
            this.loadDestinationConnections(item.tenantId);
          }.bind(this)
        );
        return Promise.all([
          connectionStringDataFetcher.getConnectionDetailInfo(sourceId, true, { defaultChecked: false }),
          this.item.getSelectedQuerySources()
        ])
          .then(
            function(results) {
              var dbResult = results[0];
              var selectedQuerySources = results[1];
              this.item.setPropertyValue('sourceHashCode', dbResult.hashCode);
              this.item.populateCheckedQuerySources(dbResult.connections, selectedQuerySources);
              // Refresh data source tree in advanced copy mode
              if (this.item.selectedTenant) {
                this.item.selectedTenant.readSourceConnections(this.item.sourceConnections);
                this.item.selectedTenant.setDirty();
              } else {
                this.item.resetWorkspaceTenantsStatus();
              }
            }.bind(this)
          )
          .then(
            function() {
              this.loadReportAfterConcurrency();
              this.loadDashboardAfterConcurrency();

              return Promise.resolve();
            }.bind(this)
          );
      } else {
        // reset tenant to need validation
        var tenant = find({ tenantId: detail.tenantId }, this.item.workspaceTenants);
        tenant && tenant.setDirty();
        // Workspace Tenant Destination Connection Concurrency
        this.loadReportAfterConcurrency();
        this.loadDashboardAfterConcurrency();
        return this.loadDestinationConnections(detail.tenantId);
      }
    } else {
      this.loadReportAfterConcurrency();
      this.loadDashboardAfterConcurrency();
      return Promise.resolve(true);
    }
  });

  /**
   * Load reports after concurrency
   */
  WorkspaceDetailStore.prototype.loadReportAfterConcurrency = m.action(function() {
    if (this.item.copyReport) {
      this.item.getSourceReportsPrev();
      this.item.getSourceTemplatesPrev();
      this.item.resetWorkspaceTenantsStatus();
      this.loadReports(this.item.sourceId, false);
    }
  });

  /**
   * Load dashboards after concurrency
   */
  WorkspaceDetailStore.prototype.loadDashboardAfterConcurrency = m.action(function() {
    if (this.item.copyDashboard) {
      this.item.getSourceDashboardsPrev();
      this.item.resetWorkspaceTenantsStatus();
      this.loadDashboards(this.item.sourceId, false, true);
    }
  });

  /**
   * reset Hash Code After Concurrency
   */
  WorkspaceDetailStore.prototype.resetHashCodeAfterConcurrency = m.action(function() {
    this.item.allHashcodes.advancedSettings = '';
    this.item.allHashcodes.tenantPermission = '';
  });

  /**
   * load Roles And Permissions
   * @param  {string} sourceId
   * @return {promise}
   */
  WorkspaceDetailStore.prototype.loadRolesAndPermissions = function(sourceId) {
    var workspaceDetail = this.item;
    var tenantId = sourceId || workspaceDetail.sourceId;
    tenantId = tenantId !== constants.systemSource.id ? tenantId : null;

    return roleSetupFetcher.getRoleList(tenantId, true).then(
      m.action(function getRolesAndPermissions(result) {
        workspaceDetail.populateRolesAndPermissions(result);
        return result;
      })
    );
  };

  /**
   * Load report
   * @return {Object} promise
   */
  WorkspaceDetailStore.prototype.loadReports = function(sourceId, defaultChecked) {
    var workspaceDetail = this.item;

    return Promise.join(
      reportFetcher.loadReportCategories(
        { tenantId: sourceId, type: 0, defaultChecked: defaultChecked, includeGlobalCategory: false },
        true
      ),
      reportFetcher.loadReportCategories(
        { tenantId: sourceId, type: 1, defaultChecked: defaultChecked, includeGlobalCategory: false },
        true
      ),
      m.action(function(reportList, templateList) {
        workspaceDetail.setSourceReports.call(workspaceDetail, reportList.data, templateList.data);
        workspaceDetail.setHashCodeForReports.call(workspaceDetail, reportList.hashcode, templateList.hashcode);
      })
    );
  };

  /**
   * Load dashboard
   * @param {String} sourceId
   * @param {Boolean} defaultChecked
   * @param {Boolean} forConcurrency
   * @return {Object} promise
   */
  WorkspaceDetailStore.prototype.loadDashboards = function(sourceId, defaultChecked, forConcurrency) {
    return dashboardFetcher
      .loadDashboardCategories(
        {
          tenantId: sourceId,
          defaultChecked: defaultChecked,
          includeDashboardPart: true,
          IncludeGlobalCategory: false
        },
        true
      )
      .bind(this)
      .then(
        m.action(function(dashboard) {
          this.item.setSourceDashboards(dashboard.data, forConcurrency);
          this.item.setHashCodeForDashboards(dashboard.hashcode);
        })
      );
  };

  WorkspaceDetailStore.prototype.loadReportsForCopy = function() {
    var workspaceDetail = this.item;
    var sourceId = workspaceDetail.sourceId;

    // if report list already loaded >> do nothing
    if (has('categories', workspaceDetail.sourceReports) && has('categories', workspaceDetail.sourceTemplates)) {
      return Promise.resolve();
    }

    return this.loadReports(sourceId, true);
  };

  WorkspaceDetailStore.prototype.lazyLoadingReports = m.action(function(pageInfo) {
    var workspaceDetail = this.item;

    return reportFetcher
      .lazyLoadingReports(
        {
          tenantId: workspaceDetail.sourceId,
          isUncategorized: false,
          criterias: [{ key: 'CategoryId' }],
          skipItems: pageInfo.fromIndex,
          pageSize: pageInfo.pageSize,
          parentIds: pageInfo.parentIds,
          defaultChecked: true
        },
        true
      )
      .then(
        m.action(
          function(result) {
            var listData = result.data;
            if (!isEmpty(listData)) {
              var categories = map(function(item) {
                return new CategoryDetail(item, true);
              }, listData);

              isEmpty(workspaceDetail.fakeReports) &&
                (workspaceDetail.fakeReports = {
                  name: 'All',
                  id: 'All',
                  numOfCheckedChilds: result.numOfCheckedChilds,
                  numOfChilds: result.numOfChilds,
                  checked: true,
                  indeterminate: false,
                  categories: []
                });

              workspaceDetail.isLastPage = result.isLastPage;
              LazyLoadingUtils.concatTree(
                workspaceDetail.fakeReports.categories,
                categories,
                constants.REPORT_LIST_CONCAT_TREE_MAP,
                result.numOfChilds,
                []
              );
              workspaceDetail.treeReports = LazyLoadingUtils.parseToTree(
                [workspaceDetail.fakeReports],
                constants.REPORT_LIST_TREE_MAP
              );
            }
          }.bind(this)
        )
      );
  });

  /**
   * Load data source (lazy)
   * @param {Object} pageInfo
   */
  WorkspaceDetailStore.prototype.loadLazyDataSources = m.action(function(pageInfo) {
    var workspaceDetail = this.item;
    var SourceTypeDefinitions = constants.SOURCE_TYPE_DEFINITIONS;

    /**
     * Create query source object.
     * @param {String} prefixId
     * @param {String} type
     */
    function createQuerySourceTypeObject(prefixId, type) {
      return [
        {
          id: prefixId + type,
          name: type,
          numOfCheckedChilds: 0,
          numOfChilds: 0,
          checked: false,
          indeterminate: false,
          items: []
        }
      ];
    }

    /**
     * Create query source objects.
     * @param {Object} schema
     */
    function createQuerySourceTypeObjects(schema) {
      each(function(item) {
        !schema[item.CollectionKey] && (schema[item.CollectionKey] = createQuerySourceTypeObject(schema.id, item.Key));
      }, SourceTypeDefinitions);
    }

    /**
     * Update query source object.
     * @param {Object} schema
     * @param {String} type
     */
    function updateQuerySourceTypeObject(schema, type) {
      var collection = schema[type];
      var info = collection[0];
      var config = find(
        {
          CollectionKey: type
        },
        SourceTypeDefinitions
      );

      config && (info.numOfChilds = schema[config.totalChildrenKey]);
      config && (info.numOfCheckedChilds = schema[config.numOfCheckedChildren]);
      // info.numOfCheckedChilds =  filter({
      //   checked: true
      // }, info.items).length;
      info.indeterminate =
        info.numOfChilds !== 0 && info.numOfChilds != info.numOfCheckedChilds && info.numOfCheckedChilds > 0;
    }

    /**
     * Update query source objects
     * @param {Object} schema
     */
    function updateQuerySourceTypeObjects(schema) {
      each(function(item) {
        updateQuerySourceTypeObject(schema, item.CollectionKey);
      }, SourceTypeDefinitions);
    }

    /**
     * Transform connections to display on lazy tree.
     * @param {Array} connections
     */
    function transformConnections(connections) {
      for (var i = 0; i < connections.length; i++) {
        // move out schema from dbSource
        connections[i].querySources = connections[i].dbSource.querySources;

        // loop schemas
        var schemas = connections[i].querySources;
        for (var j = 0; j < schemas.length; j++) {
          var schema = schemas[j];
          var querySources = schema.querySources;

          createQuerySourceTypeObjects(schema);

          for (var k = 0; k < querySources.length; k++) {
            var item = querySources[k];
            var config = find(
              {
                Key: item.type
              },
              SourceTypeDefinitions
            );
            config && schema[config.CollectionKey][0].items.push(item);
          }

          updateQuerySourceTypeObjects(schema);
        }
      }
      return connections;
    }

    return connectionStringDataFetcher
      .getLazyConnectionDetailInfo({
        tenantId: workspaceDetail.sourceId,
        defaultChecked: workspaceDetail.copyDataModel,
        loadField: false,
        pageIndex: pageInfo.pageIndex,
        pageSize: pageInfo.pageSize
      })
      .then(
        m.action(function(result) {
          if (!isEmpty(result.connections)) {
            !workspaceDetail.fakeDataModels.connections && (workspaceDetail.fakeDataModels.connections = []);
            var querySourceTree = new QuerySourceTree(result.connections);
            var connections = transformConnections(querySourceTree.querySources);

            LazyLoadingUtils.concatTree(
              workspaceDetail.fakeDataModels.connections,
              connections,
              constants.DATE_MODEL_CONCAT_TREE_MAP
            );
            workspaceDetail.isLastPageOfDataModel = result.isLastPage;
            workspaceDetail.treeDataModels = LazyLoadingUtils.parseToTree(
              workspaceDetail.fakeDataModels.connections,
              constants.DATE_MODEL_TREE_MAP
            );
          }
        })
      );
  });

  module.exports = WorkspaceDetailStore;
})();



// WEBPACK FOOTER //
// ./ui/logic/Izenda.WorkspaceDetailStore.js